import React, { useState, useCallback, useMemo, useRef } from "react";
import ChallengesDetailedTable from "./ChallengesDetailedTable";
import ColumnVisibilityDrawer from "./ChallengesColumnVisibilityDrawer";
import ChallengesActionsMenu from "./ChallengesActionsMenu";
import * as styles from "./ChallengesPage.module.scss";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useQueryClient } from "@tanstack/react-query";

import LoadingSpinner from "../../components/LoadingSpinner";
import { useChallengesTablePaginated } from "../../api/hooks/useChallenges";
import { useOrganization } from "../../contexts/OrganizationContext";
import { PRIVILEGES } from "../../api/types/dtos";
import { useHasPrivilege } from "../../components/authorization";
import { useFetchAllOrgIds } from "../../hooks/useFetchAllOrgIds";
import ReportRequestDialog from "../../components/reports/ReportRequestDialog";
import { usePrograms, useAllProgramVersionsDetailed } from "../../api/hooks/program.hooks";
import { useColumnVisibility } from "./ChallengesDetailedTable/ChallengesDetailedTable.columnVisibility";
import { ChallengesSearchBar } from "./components";
import CreateChallengeModal from "./CreateChallengeModal";
import SyncProgramVersionItemsModal from "./components/SyncProgramVersionItemsModal";
import { useTablePagination, useSearchWithPagination } from "../../api/hooks/useTablePagination";
import { getChallengesTablePaginated } from "../../api/services/challenge.service";
import { answerService, AnswerDto } from "../../api/services/answer.service";
import {
  applyInitialFilters,
  processCellValue,
  generateCSV,
  downloadCSV,
} from "./report-builder/utils/reportBuilder.utils";
import { ReportBuilderState } from "./report-builder/types/reportBuilder.types";
import { isEndDatePreviousYear } from "./challengeUtils";
import {
  buildChallengesCsvContent,
  buildTemplatesCsvContent,
  buildNamesCsvContent,
  buildFullExportCsvContent,
  buildOptionsCsvContent,
  downloadCsvFile,
} from "./ChallengesDetailedTable/ChallengesDetailedTable.exportCsv";
import { getSortedPvis } from "./ChallengesDetailedTable";

export const ChallengesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  // Stabilize organizationId to prevent unnecessary re-renders
  const organizationId = useMemo(() => selectedOrganization?.id, [selectedOrganization?.id]);

  // Pagination state
  const { page, pageSize, handlePageChange, handlePageSizeChange, resetPagination } =
    useTablePagination({
      initialPage: 0,
      initialPageSize: 50,
    });

  // Search state with debouncing
  const { searchTerm, debouncedSearchTerm, setSearchTerm } = useSearchWithPagination(
    1000,
    resetPagination
  );

  // Use paginated hook with search
  const {
    data: tableData,
    isLoading: isTableLoading,
    error: tableError,
    refetch: refetchTable,
  } = useChallengesTablePaginated(
    organizationId || "placeholder",
    {
      page,
      size: pageSize,
      searchTerm: debouncedSearchTerm || undefined,
    },
    {
      enabled: !!organizationId,
    }
  );

  // Programs / program versions for column visibility grouping
  const { data: programs = [] } = usePrograms(organizationId || "");
  const { data: programVersions = [] } = useAllProgramVersionsDetailed(organizationId || "");

  // Column visibility state (lifted from table for header-level control)
  const columnVisibility = useColumnVisibility({
    data: tableData?.data,
    programs,
    programVersions,
  });

  // Report generation state
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);

  // Reports from anywhere: row selection (org ids) in the challenges table
  const { hasPrivilege: canCreateReport } = useHasPrivilege(PRIVILEGES.CREATE_REPORT);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Fetch all org ids across pages with the current search filter
  const { fetchAll: fetchAllOrgIds, isLoading: selectingAllForReport, progress: selectAllProgress } =
    useFetchAllOrgIds(
      useCallback(
        async (page: number, size: number) => {
          if (!organizationId) return { items: [], totalElements: 0, totalPages: 0 };
          const response = await getChallengesTablePaginated(organizationId, {
            page,
            size,
            searchTerm: debouncedSearchTerm || undefined,
          });
          const seen = new Map<string, string>();
          (response.data?.challenges ?? []).forEach((c) => {
            if (c.inspectedOrgId) {
              seen.set(c.inspectedOrgId, c.inspectedOrgName || c.inspectedOrgId);
            }
          });
          return {
            items: Array.from(seen.entries()).map(([id, name]) => ({ id, name })),
            totalElements: (response as any).totalElements ?? 0,
            totalPages: (response as any).totalPages ?? 0,
          };
        },
        [organizationId, debouncedSearchTerm]
      )
    );

  const handleSelectAllForReport = useCallback(async () => {
    const orgs = await fetchAllOrgIds();
    if (orgs.length > 0) {
      setSelectedOrgIds(orgs.map((org) => org.id));
    }
  }, [fetchAllOrgIds]);

  // Org names for the report dialog
  const nameByOrgId = useMemo(() => {
    const map = new Map<string, string>();
    (tableData?.data?.challenges ?? []).forEach((c) => {
      if (c.inspectedOrgId) map.set(c.inspectedOrgId, c.inspectedOrgName || c.inspectedOrgId);
    });
    return map;
  }, [tableData?.data?.challenges]);

  const selectedReportOrgs = useMemo(
    () => selectedOrgIds.map((id) => ({ id, name: nameByOrgId.get(id) ?? id })),
    [selectedOrgIds, nameByOrgId]
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
    },
    [setSearchTerm]
  );

  const handleCacheInvalidation = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["challengesTablePaginated"] });
    queryClient.invalidateQueries({ queryKey: ["challenges"] });
  }, [queryClient]);

  // --- Report from file ---
  const handleReportFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !organizationId) return;

    setIsGeneratingReport(true);
    setReportError(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const configStr = e.target?.result as string;
        const config = JSON.parse(configStr) as ReportBuilderState;

        if (!config.cells || !config.cellMappings) {
          throw new Error("El archivo de configuración no es válido.");
        }

        const response = await getChallengesTablePaginated(organizationId, {
          page: 0,
          size: 10000,
        });

        if (!response.data || !response.data.challenges) {
          throw new Error("No se encontraron datos para generar el reporte.");
        }

        const { challenges } = response.data;

        const answerItemIds = challenges.flatMap((challenge) =>
          challenge.inspectionsProgramsVersionsItems
            .filter((item) => item.id)
            .map((item) => item.id)
        );

        let answersMap: Record<string, AnswerDto[]> = {};
        if (answerItemIds.length > 0) {
          try {
            const answersResponse = await answerService.getByItemIds(organizationId, answerItemIds);
            answersMap = (answersResponse as { data?: Record<string, AnswerDto[]> }).data || {};
          } catch (err) {
            console.warn("Failed to load answers, continuing without them:", err);
          }
        }

        const filteredChallenges = config.initialFilters
          ? applyInitialFilters(challenges, config.initialFilters)
          : challenges;

        const rows: string[][] = [];
        const sortedCells = [...config.cells].sort((a, b) => a.order - b.order);

        for (const challenge of filteredChallenges) {
          const row: string[] = [];
          for (const cell of sortedCells) {
            const mapping = config.cellMappings.find((m) => m.cellId === cell.id);
            if (mapping) {
              const processed = processCellValue(challenge, mapping, answersMap);
              row.push(processed.value || "");
            } else {
              row.push("");
            }
          }
          rows.push(row);
        }

        const headers = sortedCells.map((c) => c.name);
        const processedRows = rows.map((row) =>
          row.map((value, index) => ({
            cellId: sortedCells[index]?.id || "",
            value: value || "",
          }))
        );
        const csv = generateCSV(headers, processedRows);

        const timestamp = new Date().toISOString().split("T")[0];
        downloadCSV(csv, `reporte_retos_${timestamp}.csv`);
      } catch (err: unknown) {
        console.error("Error generating report:", err);
        setReportError(err instanceof Error ? err.message : "Error generando el reporte.");
      } finally {
        setIsGeneratingReport(false);
        event.target.value = "";
      }
    };
    reader.readAsText(file);
  };

  // --- Menu actions ---
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const menuActions = useMemo(
    () => ({
      reportFromFile: () => {
        handleMenuClose();
        fileInputRef.current?.click();
      },
      reportFromTemplate: () => {
        handleMenuClose();
        setReportDialogOpen(true);
      },
      reportBuilder: () => {
        handleMenuClose();
        navigate("/challenges/report-builder");
      },
      syncItems: () => {
        handleMenuClose();
        setSyncModalOpen(true);
      },
      createChallenge: () => {
        handleMenuClose();
        setCreateModalOpen(true);
      },
    }),
    [handleMenuClose, navigate]
  );

  // --- CSV Download handlers ---
  const csvHandlers = useMemo(() => {
    const download = (builder: () => string, filename: string) => {
      handleMenuClose();
      if (!tableData?.data) return;
      downloadCsvFile(builder(), filename);
    };
    return {
      downloadCsv: () => download(
        () => buildChallengesCsvContent(tableData!.data!, {}, getSortedPvis, isEndDatePreviousYear),
        "challenges_export.csv",
      ),
      downloadTemplates: () => download(
        () => buildTemplatesCsvContent(tableData!.data!, getSortedPvis),
        "challenge_templates.csv",
      ),
      downloadNames: () => download(
        () => buildNamesCsvContent(tableData!.data!, getSortedPvis),
        "challenge_names.csv",
      ),
      downloadFullExport: () => download(
        () => buildFullExportCsvContent(tableData!.data!),
        "challenges_full_export.csv",
      ),
      downloadOptions: () => download(
        () => buildOptionsCsvContent(tableData!.data!),
        "challenge_options.csv",
      ),
    };
  }, [tableData, handleMenuClose]);

  // Loading state - only show spinner on initial load
  const isLoading = isTableLoading && !tableData;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container maxWidth="xl">
      <div className={styles.container}>
        {/* Hidden file input for report-from-file */}
        <input
          type="file"
          hidden
          accept=".json"
          ref={fileInputRef}
          onChange={handleReportFileUpload}
        />

        <Box className={styles.header}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <h1 className={styles.title}>Retos</h1>
            {(tableData?.data as any)?.totalElements > 0 && (
              <Typography variant="body2" color="text.secondary">
                {(tableData?.data as any).totalElements} registros encontrados
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Por página</InputLabel>
              <Select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value as number)}
                label="Por página"
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Visibilidad de columnas">
              <IconButton
                onClick={() => columnVisibility.setColumnSelectorOpen(true)}
                size="small"
              >
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate("/challenges/manage")}
            >
              Gestión
            </Button>
            <ChallengesActionsMenu
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onOpen={(el) => setMenuAnchorEl(el)}
              onClose={handleMenuClose}
              menuActions={menuActions}
              csvHandlers={csvHandlers}
              isGeneratingReport={isGeneratingReport}
              hasChallenges={!!tableData?.data?.challenges?.length}
              hasProgramVersionItems={!!tableData?.data?.programVersionItems?.length}
              templateDownloadEnabled={!!import.meta.env.VITE_ENABLE_CHALLENGE_TEMPLATE_DOWNLOAD}
            />
          </Box>
        </Box>

        {/* Modals & Dialogs */}
        {createModalOpen && (
          <CreateChallengeModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            onSuccess={() => {
              setCreateModalOpen(false);
              handleCacheInvalidation();
            }}
          />
        )}

        {syncModalOpen && (
          <SyncProgramVersionItemsModal
            open={syncModalOpen}
            onClose={() => setSyncModalOpen(false)}
            onSuccess={handleCacheInvalidation}
          />
        )}

        {canCreateReport && (
          <ReportRequestDialog
            open={reportDialogOpen}
            onClose={() => setReportDialogOpen(false)}
            orgs={selectedReportOrgs}
            sourceLabel="Retos"
          />
        )}

        {/* Error messages displayed inline */}
        {tableError && (
          <Alert severity="error" className={styles.error} style={{ marginBottom: 24 }}>
            Error cargando tabla de retos: {tableError.message}
          </Alert>
        )}
        {reportError && (
          <Alert severity="error" onClose={() => setReportError(null)} sx={{ mb: 3 }}>
            {reportError}
          </Alert>
        )}

        {/* Search Bar */}
        <Box className={styles.searchContainer}>
          <ChallengesSearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Buscar por finca, productor, cédula, teléfono, región o grupo..."
          />
        </Box>

        {canCreateReport && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={handleSelectAllForReport}
              disabled={selectingAllForReport}
            >
              {selectingAllForReport
                ? `${selectAllProgress?.loaded ?? 0}/${selectAllProgress?.total || "?"}`
                : "Seleccionar todo el filtro"}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {selectedOrgIds.length} seleccionada{selectedOrgIds.length === 1 ? "" : "s"}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              size="small"
              onClick={() => setReportDialogOpen(true)}
              disabled={selectedOrgIds.length === 0}
            >
              Descargar Reporte
            </Button>
          </Box>
        )}

        <div className={styles.content}>
          <ChallengesDetailedTable
            data={tableData?.data}
            loading={isTableLoading}
            enableSelection={canCreateReport}
            selectedOrgIds={selectedOrgIds}
            onSelectionChange={setSelectedOrgIds}
            pagination={tableData?.data ? {
              page,
              pageSize,
              totalElements: (tableData.data as any).totalElements ?? 0,
              totalPages: (tableData.data as any).totalPages ?? 0,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            } : undefined}
            onRefetch={refetchTable}
            columnVisibilityModel={columnVisibility.columnVisibilityModel}
            onColumnVisibilityModelChange={columnVisibility.setColumnVisibilityModel}
          />
        </div>

        {/* Column Visibility Drawer */}
        <ColumnVisibilityDrawer
          open={columnVisibility.columnSelectorOpen}
          onClose={() => columnVisibility.setColumnSelectorOpen(false)}
          hasProgramVersionItems={!!tableData?.data?.programVersionItems?.length}
          columnVisibility={columnVisibility}
        />
      </div>
    </Container>
  );
};

export default ChallengesPage;
